import { useEffect, useState } from 'react';
import { seerrApi, type GuideItem, type Meta, type MiniItem, type Ratings } from '../api';
import { useInLibrary } from '../meta';
import { linkProps } from '../router';
import { RequestCard } from './RequestCard';
import { loadRequestStatuses } from '../requests';

/** Clearlogo als het er is, anders de titel als tekst. */
export function TitleLogo({ logo, title, className = '' }: { logo: string | null | undefined; title: string; className?: string }) {
  const [failed, setFailed] = useState(false);
  if (logo && !failed) {
    return (
      <h1 className={`title-logo ${className}`}>
        <img src={logo} alt={title} onError={() => setFailed(true)} />
      </h1>
    );
  }
  return <h1 className={className}>{title}</h1>;
}

const RATING_DEFS: { key: keyof Ratings; label: string; fmt: (v: number) => string; cls: string }[] = [
  { key: 'imdb', label: 'IMDb', fmt: (v) => v.toFixed(1), cls: 'r-imdb' },
  { key: 'tomatoes', label: 'Rotten Tomatoes', fmt: (v) => `${Math.round(v)}%`, cls: 'r-rt' },
  { key: 'popcorn', label: 'Publiek (RT)', fmt: (v) => `${Math.round(v)}%`, cls: 'r-pop' },
  { key: 'metacritic', label: 'Metacritic', fmt: (v) => String(Math.round(v)), cls: 'r-mc' },
  { key: 'letterboxd', label: 'Letterboxd', fmt: (v) => v.toFixed(1), cls: 'r-lb' },
  { key: 'trakt', label: 'Trakt', fmt: (v) => `${Math.round(v)}%`, cls: 'r-trakt' },
];

export function RatingsRow({ ratings }: { ratings: Ratings | undefined }) {
  if (!ratings) return null;
  const list = RATING_DEFS.filter((d) => typeof ratings[d.key] === 'number' && (ratings[d.key] as number) > 0);
  if (!list.length) return null;
  return (
    <div className="ratings" aria-label="Beoordelingen">
      {list.map((d) => (
        <span key={d.key} className={`rating ${d.cls}`}>
          <span className="rating-src">{d.label}</span>
          <span className="rating-val">{d.fmt(ratings[d.key] as number)}</span>
        </span>
      ))}
    </div>
  );
}

export function WatchProviders({ meta }: { meta: Meta | null }) {
  if (!meta?.watchProviders.length) return null;
  return (
    <div className="providers">
      <span className="subtle">Ook te streamen in NL:</span>
      {meta.watchProviders.slice(0, 6).map((p) => (
        <span key={p.name} className="provider" title={p.name}>
          {p.logo && <img src={p.logo} alt="" />}
          {p.name}
        </span>
      ))}
    </div>
  );
}

/** Trailer van YouTube (privacyvriendelijke variant) in een venster boven de pagina. */
export function TrailerModal({ youtubeKey, title, onClose }: { youtubeKey: string; title: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return (
    <div className="modal-backdrop trailer-backdrop" onClick={onClose}>
      <div className="trailer" role="dialog" aria-modal="true" aria-label={`Trailer: ${title}`} onClick={(e) => e.stopPropagation()}>
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(youtubeKey)}?autoplay=1&rel=0`}
          title={`Trailer: ${title}`}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
        />
        <button className="btn" data-autofocus onClick={onClose}>Sluiten</button>
      </div>
    </div>
  );
}

/**
 * Rij met TMDB-titels (collectie, "Meer zoals dit"): wat in je bibliotheek staat opent
 * de Plex-pagina, de rest kun je aanvragen via Seerr.
 */
export function DiscoverRow({ title, items }: { title: string; items: MiniItem[] }) {
  const found = useInLibrary(items);
  const [seerr, setSeerr] = useState<{ configured: boolean; canRequest: boolean } | null>(null);
  useEffect(() => {
    seerrApi.status().then(setSeerr).catch(() => setSeerr(null));
  }, []);
  // Wat niet in de bibliotheek staat: stand in Seerr ophalen ("Aangevraagd")
  const missingKey = items.filter((m) => found[`${m.type}:${m.tmdbId}`] === null).map((m) => `${m.type}:${m.tmdbId}`).join(',');
  useEffect(() => {
    if (!seerr?.configured || !missingKey) return;
    void loadRequestStatuses(missingKey.split(',').map((k) => ({ type: k.startsWith('tv') ? 'tv' : 'movie', tmdbId: Number(k.split(':')[1]) })));
  }, [seerr?.configured, missingKey]);
  if (!items.length) return null;
  return (
    <section className="row" aria-label={title}>
      <h2>{title}</h2>
      <div className="row-track" data-focus-group>
        {items.map((m) => {
          const k = `${m.type}:${m.tmdbId}`;
          const rk = found[k];
          if (rk) {
            return (
              <a key={k} className="card card-poster" {...linkProps(`/item/${rk}`)} aria-label={`${m.title}, in je bibliotheek`}>
                <div className="card-art" style={m.poster ? { backgroundImage: `url("${m.poster}")` } : undefined}>
                  {!m.poster && <span className="card-fallback">{m.title}</span>}
                  <span className="lib-badge own">In bibliotheek</span>
                </div>
                <span className="card-title">{m.title}</span>
                <span className="card-sub">{m.sub ?? m.year ?? ''}</span>
              </a>
            );
          }
          if (seerr?.configured && rk === null) {
            return (
              <div key={k} className="row-req">
                <RequestCard
                  m={{ tmdbId: m.tmdbId, mediaType: m.type, title: m.title, year: m.year, poster: m.poster, backdrop: m.backdrop, overview: '', rating: null, status: 'none' }}
                  canRequest={seerr.canRequest}
                />
              </div>
            );
          }
          return (
            <div key={k} className="card card-poster" aria-label={m.title}>
              <div className="card-art" style={m.poster ? { backgroundImage: `url("${m.poster}")` } : undefined}>
                {!m.poster && <span className="card-fallback">{m.title}</span>}
              </div>
              <span className="card-title">{m.title}</span>
              <span className="card-sub">{m.sub ?? m.year ?? ''}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** IMDb Parents Guide: één chip per onderwerp, de zwaarte in kleur. */
export function ParentsGuideRow({ guide }: { guide: GuideItem[] | null | undefined }) {
  if (!guide?.length) return null;
  return (
    <div className="pg-row" aria-label="Ouderinformatie (IMDb Parents Guide)">
      <span className="pg-badge" title="IMDb Parents Guide">Ouders</span>
      {guide.map((g) => (
        <span key={g.id} className="pg-chip">{g.label} <strong className={`pg-${g.level.toLowerCase()}`}>{g.levelLabel}</strong></span>
      ))}
    </div>
  );
}
